// NewlyAppliedClients.jsx
import React, { useEffect, useState } from "react";
import { FaRegEdit, FaUserGraduate } from "react-icons/fa";
import { RiDeleteBin6Line, RiExchangeFundsLine } from "react-icons/ri";
import ClientStatusModal from "../../../components/Modals/ClientStatusModal";
import DeleteClientModal from "../../../components/Modals/DeleteClientModal";
import ClientDetailsModal from "../../Modals/ClientDetailsModal";
import EditClientModal from "../../Modals/EditClientModal";
import axios from "axios";

const NewlyAppliedClients = ({ query, page, itemsPerPage, onDataChange }) => {
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isClient, setIsClient] = useState([]);

  const fetchClient = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/our-client/get-clients`
      );
      if (res?.data) setIsClient(res.data);
      else setError("No data received");
    } catch (error) {
      console.error("Fetch error:", error);
      setError("Failed to fetch Clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClient();
  }, []);

  const filteredData = isClient.filter(
    (item) =>
      item?.status === "pending" &&
      (item?.clientName || item?.name || "")
        .toLowerCase()
        .includes((query || "").toLowerCase())
  );

  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedClients = filteredData.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  useEffect(() => {
    if (onDataChange) onDataChange(totalPages, filteredData.length);
  }, [filteredData.length, totalPages, onDataChange]);

  const handleStatusClick = (client) => {
    setSelectedClient(client);
    setShowStatusModal(true);
  };

  const handleDeleteClick = (client) => {
    setSelectedClient(client);
    setShowDeleteModal(true);
  };

  const handleDetailsClick = (client) => {
    setSelectedClient(client);
    setShowDetailsModal(true);
  };

  const handleEditClick = (client) => {
    setSelectedClient(client);
    setShowEditModal(true);
  };

  const closeModals = () => {
    setShowStatusModal(false);
    setShowDeleteModal(false);
    setShowDetailsModal(false);
    setShowEditModal(false);
    setSelectedClient(null);
    fetchClient();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedClient || !selectedClient._id) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/our-client/delete-client/${selectedClient._id}`,
        {
          headers: { "Content-Type": "application/json" },
          data: { email: selectedClient.email || "" },
        }
      );
      closeModals();
    } catch (error) {
      console.error("Delete error:", error);
      alert("Failed to delete client.");
    }
  };

  return (
    <section className="px-2 mb-4">
      {/* ✅ Skeleton Loader */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 py-2">
          {Array.from({ length: itemsPerPage }).map((_, idx) => (
            <div
              key={idx}
              className="animate-pulse bg-white p-5 rounded-lg shadow-lg flex flex-col items-center text-center"
            >
              <div className="absolute top-2 right-2 flex gap-2">
                <div className="w-5 h-5 bg-gray-300 rounded"></div>
                <div className="w-5 h-5 bg-gray-300 rounded"></div>
              </div>
              <div className="absolute top-2 left-2 w-12 h-4 bg-gray-300 rounded"></div>
              <div className="w-20 h-20 bg-gray-300 rounded-full mb-4"></div>
              <div className="h-4 w-24 bg-gray-300 rounded mb-2"></div>
              <div className="h-3 w-32 bg-gray-200 rounded mb-4"></div>
              <div className="w-full flex justify-between gap-4 px-4">
                <div className="w-full h-8 bg-gray-300 rounded"></div>
                <div className="w-8 h-8 bg-gray-300 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      ) : paginatedClients.length === 0 ? (
        <div className="flex justify-center">
          <p className="p-4 text-gray-500">No newly applied clients found.</p>
        </div>
      ) : (
        <div className="overflow-x-auto sm:overflow-visible">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 py-2">
            {paginatedClients.map((client, index) => (
              <div
                key={index}
                className="relative bg-white p-5 rounded-lg shadow-lg flex flex-col items-center text-center"
              >
                <div className="absolute top-2 right-2 flex gap-2">
                  <button
                    className="text-blue-600"
                    onClick={() => handleEditClick(client)}
                  >
                    <FaRegEdit className="h-5 w-5" />
                  </button>
                  <button
                    className="text-red-600"
                    onClick={() => handleDeleteClick(client)}
                  >
                    <RiDeleteBin6Line className="h-5 w-5" />
                  </button>
                </div>
                <p className="absolute top-2 left-2 bg-gradient-to-r from-gray-500 via-gray-600 to-gray-700 rounded-2xl text-white text-xs py-1 px-2">
                  New
                </p>
                <FaUserGraduate className="w-20 h-20 rounded-full mb-4 border-2 border-white shadow-md text-[#1B81B2]" />
                <h3 className="font-semibold text-gray-900">
                  {client.clientName || client.name}
                </h3>
                <p className="text-sm text-blue-600 mt-1 bg-blue-50 px-3 py-1 rounded-full">
                  {client.email}
                </p>
                <div className="w-full flex justify-between gap-4 px-4">
                  <div className="w-full">
                    <button
                      type="button"
                      onClick={() => handleDetailsClick(client)}
                      className="mt-6 w-full bg-gradient-to-r from-[#0070a8] to-[#023955] hover:from-[#1e8cbf] hover:to-[#1674a3] text-white tracking-wide font-medium py-1.5 rounded-lg text-sm shadow-md hover:shadow-lg transition-all duration-300"
                    >
                      Details
                    </button>
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={() => handleStatusClick(client)}
                      className="mt-6 w-full bg-gradient-to-r from-[#0070a8] to-[#023955] hover:from-[#1e8cbf] hover:to-[#1674a3] text-white tracking-wide font-medium px-1.5 py-1.5 rounded-lg text-sm md:text-base shadow-md hover:shadow-lg transition-all duration-300"
                      aria-label="Change client status"
                    >
                      <RiExchangeFundsLine size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showStatusModal && selectedClient && (
        <ClientStatusModal client={selectedClient} onClose={closeModals} />
      )}
      {showDeleteModal && selectedClient && (
        <DeleteClientModal
          itemName={selectedClient.clientName || selectedClient.name}
          onClose={closeModals}
          onConfirm={handleDeleteConfirm}
        />
      )}
      {showDetailsModal && selectedClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 overflow-y-auto">
          <div className="max-w-full w-full sm:max-w-lg">
            <ClientDetailsModal client={selectedClient} onClose={closeModals} />
          </div>
        </div>
      )}
      {showEditModal && selectedClient && (
        <EditClientModal
          client={selectedClient}
          onClose={closeModals}
          onSubmit={() => {
            fetchClient();
            closeModals();
          }}
        />
      )}
    </section>
  );
};

export default NewlyAppliedClients;
